if (!customElements.get('agency-contact-hero')) {
  class AgencyContactHero extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.contactDrawerOpen = Boolean(document.querySelector('agency-contact-drawer dialog[open]'));
      this.setupHeroCarousel();
      this.setupHeroMotionControl();
      document.addEventListener('blend:contact-drawer-state', (event) => {
        this.contactDrawerOpen = event.detail.open;
        if (this.contactDrawerOpen) this.stopCarouselAutoplay();
        else this.scheduleCarouselAutoplay();
      }, { signal: this.signal });
    }

    disconnectedCallback() {
      this.resetCarouselGlare?.({ immediate: true });
      this.abortController?.abort();
      this.heroMotionObserver?.disconnect();
      this.heroMotionObserver = null;
      this.stopCarouselAutoplay();
    }

    setupHeroMotionControl() {
      this.heroMotionToggle = this.querySelector('[data-hero-motion-toggle]');
      if (!this.heroMotionToggle) return;
      this.heroMotionPaused = this.heroMotionPaused === true;
      this.heroMotionInView = true;
      if (window.IntersectionObserver) {
        this.heroMotionObserver = new window.IntersectionObserver(([entry]) => {
          this.heroMotionInView = entry.isIntersecting;
          this.updateHeroMotionControl();
        });
        this.heroMotionObserver.observe(this);
      }
      this.heroMotionToggle.addEventListener('click', () => {
        this.heroMotionPaused = !this.heroMotionPaused;
        this.updateHeroMotionControl();
      }, { signal: this.signal });
      this.reducedMotionQuery.addEventListener('change', () => this.updateHeroMotionControl(), { signal: this.signal });
      document.addEventListener('visibilitychange', () => this.updateHeroMotionControl(), { signal: this.signal });
      this.updateHeroMotionControl();
    }

    updateHeroMotionControl() {
      if (!this.heroMotionToggle) return;
      const unavailable = this.reducedMotionQuery.matches || Boolean(window.Shopify?.designMode);
      this.heroMotionToggle.hidden = unavailable;
      const paused = this.heroMotionPaused || unavailable || document.hidden || this.heroMotionInView === false;
      this.toggleAttribute('data-hero-motion-paused', paused);
      const label = this.heroMotionPaused ? 'Resume automatic motion' : 'Pause automatic motion';
      this.heroMotionToggle.setAttribute('aria-label', label);
      this.heroMotionToggle.title = label;
      if (paused) this.stopCarouselAutoplay();
      else this.scheduleCarouselAutoplay();
    }

    setupHeroCarousel() {
      this.carousel = this.querySelector('[data-hero-carousel]');
      if (!this.carousel) return;

      this.carouselViewport = this.carousel.querySelector('[data-carousel-viewport]');
      this.carouselSlides = Array.from(this.carousel.querySelectorAll('[data-carousel-slide]'));
      this.carouselDots = Array.from(this.carousel.querySelectorAll('[data-carousel-dot]'));
      this.carouselPrevious = this.carousel.querySelector('[data-carousel-previous]');
      this.carouselNext = this.carousel.querySelector('[data-carousel-next]');
      this.carouselStatus = this.carousel.querySelector('[data-carousel-status]');
      this.carouselIndex = 0;
      this.carouselDirection = 1;
      this.carouselPointerPaused = false;
      this.carouselFocusPaused = false;
      this.carouselDragging = false;
      this.carouselPointerStartX = null;
      this.carouselInterval = Math.max(3000, Number(this.carousel.dataset.carouselInterval) || 6000);
      this.carouselAutoplayEnabled =
        this.carousel.dataset.carouselAutoplay === 'true' &&
        this.carouselSlides.length > 1 &&
        !window.Shopify?.designMode;

      const hasMultipleSlides = this.carouselSlides.length > 1;
      if (this.carouselPrevious) this.carouselPrevious.hidden = !hasMultipleSlides;
      if (this.carouselNext) this.carouselNext.hidden = !hasMultipleSlides;

      if (!this.carouselViewport || !this.carouselSlides.length) return;

      this.updateCarousel(0, { announce: false });
      this.setupCarouselGlare();

      this.carouselPrevious?.addEventListener(
        'click',
        () => this.updateCarousel(this.carouselIndex - 1, { announce: true }),
        { signal: this.signal }
      );
      this.carouselNext?.addEventListener(
        'click',
        () => this.updateCarousel(this.carouselIndex + 1, { announce: true }),
        { signal: this.signal }
      );
      this.carouselDots.forEach((dot) => {
        dot.addEventListener(
          'click',
          () => this.updateCarousel(Number(dot.dataset.carouselDot), { announce: true }),
          { signal: this.signal }
        );
      });
      this.carousel.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const direction = event.key === 'ArrowLeft' ? -1 : 1;
          this.updateCarousel(this.carouselIndex + direction, { announce: true });
        },
        { signal: this.signal }
      );
      this.carousel.addEventListener(
        'mouseenter',
        () => {
          this.carouselPointerPaused = true;
          this.stopCarouselAutoplay();
        },
        { signal: this.signal }
      );
      this.carousel.addEventListener(
        'mouseleave',
        () => {
          this.carouselPointerPaused = false;
          this.scheduleCarouselAutoplay();
        },
        { signal: this.signal }
      );
      this.carouselViewport.addEventListener(
        'pointerdown',
        (event) => {
          if (event.button !== undefined && event.button !== 0) return;
          this.carouselDragging = true;
          this.carouselPointerStartX = event.clientX;
          this.carouselPointerId = event.pointerId;
          this.carouselPointerSlide = event.target.closest?.('[data-carousel-slide]') || null;
          this.carouselViewport.setPointerCapture?.(event.pointerId);
          this.stopCarouselAutoplay();
        },
        { signal: this.signal }
      );
      this.carouselViewport.addEventListener(
        'pointerup',
        (event) => this.finishCarouselDrag(event),
        { signal: this.signal }
      );
      this.carouselViewport.addEventListener(
        'pointercancel',
        () => {
          this.carouselDragging = false;
          this.carouselPointerStartX = null;
          this.carouselPointerId = null;
          this.carouselPointerSlide = null;
          this.scheduleCarouselAutoplay();
        },
        { signal: this.signal }
      );
      this.carousel.addEventListener(
        'focusin',
        () => {
          this.carouselFocusPaused = true;
          this.stopCarouselAutoplay();
        },
        { signal: this.signal }
      );
      this.carousel.addEventListener(
        'focusout',
        (event) => {
          if (event.relatedTarget && this.carousel.contains(event.relatedTarget)) return;
          this.carouselFocusPaused = false;
          this.scheduleCarouselAutoplay();
        },
        { signal: this.signal }
      );
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) this.stopCarouselAutoplay();
          else this.scheduleCarouselAutoplay();
        },
        { signal: this.signal }
      );
      this.reducedMotionQuery.addEventListener(
        'change',
        () => {
          if (this.reducedMotionQuery.matches) {
            this.resetCarouselGlare?.({ immediate: true });
            this.stopCarouselAutoplay();
          }
          else this.scheduleCarouselAutoplay();
        },
        { signal: this.signal }
      );
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          const blockId = event.detail?.blockId;
          const slideIndex = this.carouselSlides.findIndex((slide) => slide.dataset.carouselBlockId === blockId);
          if (slideIndex !== -1) this.updateCarousel(slideIndex, { announce: false });
        },
        { signal: this.signal }
      );

      this.scheduleCarouselAutoplay();
    }

    updateCarousel(index, options = {}) {
      if (!this.carouselSlides?.length) return;

      const { announce = false } = options;
      const total = this.carouselSlides.length;
      const normalizedIndex = ((Number(index) % total) + total) % total;
      let movement = normalizedIndex - this.carouselIndex;
      if (Math.abs(movement) > total / 2) movement += movement > 0 ? -total : total;
      if (movement !== 0) this.resetCarouselGlare?.();
      if (movement !== 0) this.carouselDirection = Math.sign(movement);
      this.carouselIndex = normalizedIndex;

      this.carouselSlides.forEach((slide, slideIndex) => {
        const isCurrent = slideIndex === normalizedIndex;
        let relativePosition = (slideIndex - normalizedIndex + total) % total;
        if (relativePosition > total / 2) relativePosition -= total;
        if (total % 2 === 0 && relativePosition === total / 2) {
          relativePosition *= this.carouselDirection || 1;
        }
        const boundedPosition = Math.max(-3, Math.min(3, relativePosition));

        slide.dataset.carouselPosition = String(boundedPosition);
        slide.classList.toggle('is-current', isCurrent);
        if (isCurrent) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      this.carouselDots?.forEach((dot, dotIndex) => {
        const isCurrent = dotIndex === normalizedIndex;
        dot.classList.toggle('is-current', isCurrent);
        if (isCurrent) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (announce && this.carouselStatus) {
        this.carouselStatus.textContent = `Image ${normalizedIndex + 1} of ${total}`;
      }

      this.scheduleCarouselAutoplay();
    }

    finishCarouselDrag(event) {
      if (!this.carouselDragging) return;

      const startX = this.carouselPointerStartX;
      const distance = Number.isFinite(startX) ? event.clientX - startX : 0;
      const pointerSlide = this.carouselPointerSlide;
      this.carouselDragging = false;
      this.carouselPointerStartX = null;
      this.carouselPointerId = null;
      this.carouselPointerSlide = null;

      if (Math.abs(distance) >= 48) {
        this.updateCarousel(this.carouselIndex + (distance < 0 ? 1 : -1), { announce: true });
      } else if (Math.abs(distance) < 12 && pointerSlide) {
        const slideIndex = this.carouselSlides.indexOf(pointerSlide);
        if (slideIndex !== -1 && slideIndex !== this.carouselIndex) {
          this.updateCarousel(slideIndex, { announce: true });
          return;
        }
        this.scheduleCarouselAutoplay();
      } else {
        this.scheduleCarouselAutoplay();
      }
    }

    setupCarouselGlare() {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      this.carouselGlareEntries = [];
      this.carouselGlareFrame = null;
      this.carouselGlarePending = null;
      this.carouselGlareTimers = new Map();

      const writeGlare = ({ card, x, y }) => {
        const deltaX = x - 50;
        const deltaY = y - 50;
        const rotateX = deltaY * 0.2;
        const rotateY = deltaX * -0.1142857143;
        const backgroundX = 37.5 + x / 4;
        const backgroundY = 33.333333 + y / 3;

        card.style.setProperty('--agency-glare-x', `${x}%`);
        card.style.setProperty('--agency-glare-y', `${y}%`);
        card.style.setProperty('--agency-glare-bg-x', `${backgroundX}%`);
        card.style.setProperty('--agency-glare-bg-y', `${backgroundY}%`);
        card.style.setProperty('--agency-glare-rotate-x', `${rotateX}deg`);
        card.style.setProperty('--agency-glare-rotate-y', `${rotateY}deg`);
      };

      const queueGlare = (card, x, y) => {
        this.carouselGlarePending = { card, x, y };
        if (this.carouselGlareFrame !== null) return;

        this.carouselGlareFrame = window.requestAnimationFrame(() => {
          const pending = this.carouselGlarePending;
          this.carouselGlareFrame = null;
          this.carouselGlarePending = null;
          if (pending) writeGlare(pending);
        });
      };

      const clearTrackingTimer = (card) => {
        const timer = this.carouselGlareTimers.get(card);
        if (timer !== undefined) window.clearTimeout(timer);
        this.carouselGlareTimers.delete(card);
      };

      const resetGlare = (slide, card, options = {}) => {
        const { immediate = false } = options;
        clearTrackingTimer(card);

        if (this.carouselGlarePending?.card === card) {
          this.carouselGlarePending = null;
          if (this.carouselGlareFrame !== null) window.cancelAnimationFrame(this.carouselGlareFrame);
          this.carouselGlareFrame = null;
        }

        slide.classList.remove('is-glare-active', 'is-glare-tracking');
        if (immediate) card.style.setProperty('--agency-glare-duration', '0ms');
        else card.style.removeProperty('--agency-glare-duration');
        card.style.setProperty('--agency-glare-x', '50%');
        card.style.setProperty('--agency-glare-y', '50%');
        card.style.setProperty('--agency-glare-bg-x', '50%');
        card.style.setProperty('--agency-glare-bg-y', '50%');
        card.style.setProperty('--agency-glare-rotate-x', '0deg');
        card.style.setProperty('--agency-glare-rotate-y', '0deg');
      };

      this.carouselSlides.forEach((slide) => {
        const card = slide.querySelector('[data-carousel-glare-card]');
        if (!card) return;
        this.carouselGlareEntries.push({ slide, card });
        let cardRect = null;

        slide.addEventListener(
          'pointerenter',
          (event) => {
            if (event.pointerType !== 'mouse' || this.reducedMotionQuery.matches) return;
            cardRect = card.getBoundingClientRect();
            if (!cardRect.width || !cardRect.height) return;

            clearTrackingTimer(card);
            slide.classList.add('is-glare-active');
            slide.classList.remove('is-glare-tracking');
            card.style.removeProperty('--agency-glare-duration');
            const timer = window.setTimeout(() => {
              if (!slide.classList.contains('is-glare-active')) return;
              slide.classList.add('is-glare-tracking');
              this.carouselGlareTimers.delete(card);
            }, 300);
            this.carouselGlareTimers.set(card, timer);
          },
          { signal: this.signal }
        );
        slide.addEventListener(
          'pointermove',
          (event) => {
            if (event.pointerType !== 'mouse' || this.reducedMotionQuery.matches) return;
            if (!cardRect?.width || !cardRect?.height) return;

            const x = Math.max(0, Math.min(100, ((event.clientX - cardRect.left) / cardRect.width) * 100));
            const y = Math.max(0, Math.min(100, ((event.clientY - cardRect.top) / cardRect.height) * 100));

            slide.classList.add('is-glare-active');
            queueGlare(card, x, y);
          },
          { signal: this.signal }
        );
        slide.addEventListener(
          'pointerleave',
          () => {
            cardRect = null;
            resetGlare(slide, card);
          },
          { signal: this.signal }
        );
      });

      this.resetCarouselGlare = (options = {}) => {
        this.carouselGlareEntries.forEach(({ slide, card }) => resetGlare(slide, card, options));
      };
    }

    shouldCarouselAutoplay() {
      return Boolean(
        this.carouselAutoplayEnabled &&
          !this.heroMotionPaused &&
          this.heroMotionInView !== false &&
          this.carouselSlides?.length > 1 &&
          !this.reducedMotionQuery.matches &&
          !document.hidden &&
          !this.carouselPointerPaused &&
          !this.carouselFocusPaused &&
          !this.carouselDragging &&
          !this.contactDrawerOpen
      );
    }

    scheduleCarouselAutoplay() {
      this.stopCarouselAutoplay();
      if (!this.shouldCarouselAutoplay()) return;

      this.carouselTimer = window.setTimeout(() => {
        this.updateCarousel(this.carouselIndex + 1, { announce: false });
      }, this.carouselInterval);
    }

    stopCarouselAutoplay() {
      window.clearTimeout(this.carouselTimer);
    }

    track(eventName, detail = {}) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...detail });
    }
  }

  customElements.define('agency-contact-hero', AgencyContactHero);
}
